import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import {
    DollarSign,
    TrendingUp,
    TrendingDown,
    Calendar,
    FileText,
    Clock,
    CheckCircle,
    XCircle,
    AlertCircle,
    CreditCard,
    Filter,
    Download,
    Search,
    Loader2,
    ArrowUpRight,
    ArrowDownRight,
    Eye,
    MoreVertical,
    RefreshCw,
    Crown,
} from 'lucide-react';
import { CreateChargeModal } from '../../components/CreateChargeModal';

interface Invoice {
    id: string;
    amount_cents: number;
    currency: string;
    status: string;
    due_date: string;
    paid_at: string | null;
    description: string | null;
    created_at: string;
    user: {
        full_name: string;
        email: string;
    } | null;
}

interface Payment {
    id: string;
    amount_cents: number;
    currency: string;
    status: string;
    payment_method: string;
    processed_at: string | null;
    created_at: string;
}

interface FinancialStats {
    totalRevenue: number;
    monthlyRevenue: number;
    pendingAmount: number;
    activeSubscriptions: number;
    overdueInvoices: number;
}

export function FinancialPage() {
    const { tenant } = useAuth();
    const [period, setPeriod] = useState<'month' | 'quarter' | 'year'>('month');
    const [statusFilter, setStatusFilter] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [isOnboarding, setIsOnboarding] = useState(false);
    const queryClient = useQueryClient();

    // Fetch financial stats
    const { data: stats, isLoading: isLoadingStats } = useQuery({
        queryKey: ['financial-stats', tenant?.id, period],
        queryFn: async (): Promise<FinancialStats> => {
            if (!tenant?.id) return {
                totalRevenue: 0,
                monthlyRevenue: 0,
                pendingAmount: 0,
                activeSubscriptions: 0,
                overdueInvoices: 0,
            };

            // Get date range
            const now = new Date();
            let startDate = new Date();
            if (period === 'month') {
                startDate.setMonth(now.getMonth() - 1);
            } else if (period === 'quarter') {
                startDate.setMonth(now.getMonth() - 3);
            } else {
                startDate.setFullYear(now.getFullYear() - 1);
            }

            // Fetch invoices
            const { data: invoices } = await supabase
                .from('invoices')
                .select('amount_cents, status, due_date')
                .eq('tenant_id', tenant.id);

            // Fetch subscriptions
            const { data: subscriptions } = await supabase
                .from('subscriptions')
                .select('id, status')
                .eq('tenant_id', tenant.id)
                .eq('status', 'active');

            const paidInvoices = invoices?.filter((i) => i.status === 'paid') || [];
            const pendingInvoices = invoices?.filter((i) => i.status === 'pending') || [];
            const overdueInvoices = invoices?.filter(
                (i) => i.status === 'pending' && new Date(i.due_date) < now
            ) || [];

            const totalRevenue = paidInvoices.reduce((sum, i) => sum + i.amount_cents, 0);
            const pendingAmount = pendingInvoices.reduce((sum, i) => sum + i.amount_cents, 0);

            return {
                totalRevenue: totalRevenue / 100,
                monthlyRevenue: totalRevenue / 100 / (period === 'month' ? 1 : period === 'quarter' ? 3 : 12),
                pendingAmount: pendingAmount / 100,
                activeSubscriptions: subscriptions?.length || 0,
                overdueInvoices: overdueInvoices.length,
            };
        },
        enabled: !!tenant?.id,
    });

    const handleAsaasOnboarding = async () => {
        try {
            setIsOnboarding(true);
            const { data, error } = await supabase.functions.invoke('asaas-onboarding');
            if (error) throw error;

            alert('Conta Asaas conectada com sucesso! Agora você pode gerar cobranças.');
            queryClient.invalidateQueries({ queryKey: ['auth-user'] });
            window.location.reload();
        } catch (error: any) {
            console.error('Error onboarding:', error);
            alert(`Erro ao conectar Asaas: ${error.message}`);
        } finally {
            setIsOnboarding(false);
        }
    };

    // Fetch invoices
    const { data: invoices, isLoading: isLoadingInvoices } = useQuery({
        queryKey: ['invoices', tenant?.id, statusFilter],
        queryFn: async () => {
            if (!tenant?.id) return [];

            let query = supabase
                .from('invoices')
                .select(`
                    *,
                    user:user_id (full_name, email)
                `)
                .eq('tenant_id', tenant.id)
                .order('created_at', { ascending: false })
                .limit(50);

            if (statusFilter) {
                query = query.eq('status', statusFilter);
            }

            const { data, error } = await query;
            if (error) throw error;
            return data as Invoice[];
        },
        enabled: !!tenant?.id,
    });

    // Filter invoices
    const filteredInvoices = invoices?.filter((inv) =>
        inv.user?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(value);
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'paid':
                return (
                    <span className="badge bg-green-100 text-green-700 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> Pago
                    </span>
                );
            case 'pending':
                return (
                    <span className="badge bg-yellow-100 text-yellow-700 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Pendente
                    </span>
                );
            case 'overdue':
                return (
                    <span className="badge bg-red-100 text-red-700 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> Vencido
                    </span>
                );
            case 'canceled':
                return (
                    <span className="badge bg-gray-100 text-gray-700 flex items-center gap-1">
                        <XCircle className="w-3 h-3" /> Cancelado
                    </span>
                );
            default:
                return <span className="badge badge-secondary">{status}</span>;
        }
    };

    if (isLoadingStats) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="section-title flex items-center gap-2">
                        <DollarSign className="w-6 h-6" />
                        Financeiro
                        <span className="badge badge-primary">PRO</span>
                    </h1>
                    <p className="section-subtitle">
                        Gerencie cobranças e acompanhe sua receita
                    </p>
                </div>
                <div className="flex gap-2">
                    {(['month', 'quarter', 'year'] as const).map((p) => (
                        <button
                            key={p}
                            onClick={() => setPeriod(p)}
                            className={`btn ${period === p ? 'btn-primary' : 'btn-secondary'}`}
                        >
                            {p === 'month' ? 'Mês' : p === 'quarter' ? 'Trimestre' : 'Ano'}
                        </button>
                    ))}
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <StatCard
                    title="Receita Total"
                    value={formatCurrency(stats?.totalRevenue || 0)}
                    icon={<DollarSign className="w-5 h-5" />}
                    trend={12.5}
                    color="primary"
                />
                <StatCard
                    title="Média Mensal"
                    value={formatCurrency(stats?.monthlyRevenue || 0)}
                    icon={<TrendingUp className="w-5 h-5" />}
                    trend={8.2}
                    color="green"
                />
                <StatCard
                    title="Pendente"
                    value={formatCurrency(stats?.pendingAmount || 0)}
                    icon={<Clock className="w-5 h-5" />}
                    trend={-5.3}
                    color="yellow"
                />
                <StatCard
                    title="Assinaturas Ativas"
                    value={String(stats?.activeSubscriptions || 0)}
                    icon={<Crown className="w-5 h-5" />}
                    color="purple"
                />
            </div>

            {/* Alerts */}
            {(stats?.overdueInvoices || 0) > 0 && (
                <div className="card bg-red-50 border-red-200">
                    <div className="flex items-center gap-3">
                        <AlertCircle className="w-5 h-5 text-red-600" />
                        <div className="flex-1">
                            <p className="font-medium text-red-900">
                                {stats?.overdueInvoices} fatura(s) vencida(s)
                            </p>
                            <p className="text-sm text-red-700">
                                Entre em contato com os alunos para regularizar
                            </p>
                        </div>
                        <button className="btn-ghost text-red-600">
                            Ver Todas
                        </button>
                    </div>
                </div>
            )}

            {/* Asaas Onboarding Alert */}
            {!tenant?.asaas_wallet_id && (
                <div className="card bg-primary-50 border-primary-100 p-6">
                    <div className="flex flex-col md:flex-row items-center gap-6">
                        <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-sm">
                            <CreditCard className="w-8 h-8 text-primary-600" />
                        </div>
                        <div className="flex-1 text-center md:text-left">
                            <h3 className="text-lg font-bold text-gray-900">Ative seu Faturamento</h3>
                            <p className="text-gray-600 text-sm mt-1">
                                Conecte sua conta ao Asaas para começar a receber pagamentos dos seus atletas via Pix e Boleto de forma automatizada com split de taxas.
                            </p>
                        </div>
                        <button
                            onClick={handleAsaasOnboarding}
                            disabled={isOnboarding}
                            className="btn-primary"
                        >
                            {isOnboarding ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                                <RefreshCw className="w-4 h-4 mr-2" />
                            )}
                            Configurar Recebimentos
                        </button>
                    </div>
                </div>
            )}

            {/* Invoices Table */}
            <div className="card">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <h2 className="font-semibold flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Faturas Recentes
                    </h2>
                    <div className="flex gap-2">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Buscar..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="input pl-9 py-2 text-sm w-48"
                            />
                        </div>
                        <select
                            value={statusFilter || ''}
                            onChange={(e) => setStatusFilter(e.target.value || null)}
                            className="input py-2 text-sm"
                        >
                            <option value="">Todos Status</option>
                            <option value="paid">Pago</option>
                            <option value="pending">Pendente</option>
                            <option value="overdue">Vencido</option>
                            <option value="canceled">Cancelado</option>
                        </select>
                    </div>
                </div>

                {isLoadingInvoices ? (
                    <div className="flex justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
                    </div>
                ) : filteredInvoices && filteredInvoices.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="border-b text-left text-sm text-gray-500">
                                    <th className="pb-3 font-medium">Aluno</th>
                                    <th className="pb-3 font-medium">Valor</th>
                                    <th className="pb-3 font-medium">Vencimento</th>
                                    <th className="pb-3 font-medium">Status</th>
                                    <th className="pb-3 font-medium">Descrição</th>
                                    <th className="pb-3 font-medium"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {filteredInvoices.map((invoice) => (
                                    <tr key={invoice.id} className="hover:bg-gray-50">
                                        <td className="py-4">
                                            <div>
                                                <p className="font-medium text-gray-900">
                                                    {invoice.user?.full_name || 'N/A'}
                                                </p>
                                                <p className="text-sm text-gray-500">
                                                    {invoice.user?.email}
                                                </p>
                                            </div>
                                        </td>
                                        <td className="py-4 font-semibold">
                                            {formatCurrency(invoice.amount_cents / 100)}
                                        </td>
                                        <td className="py-4 text-gray-600">
                                            {new Date(invoice.due_date).toLocaleDateString('pt-BR')}
                                        </td>
                                        <td className="py-4">
                                            {getStatusBadge(invoice.status)}
                                        </td>
                                        <td className="py-4 text-gray-600 text-sm max-w-[200px] truncate">
                                            {invoice.description || '-'}
                                        </td>
                                        <td className="py-4">
                                            <button className="p-2 hover:bg-gray-100 rounded-lg">
                                                <MoreVertical className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="text-center py-12">
                        <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                        <h3 className="font-medium text-gray-900 mb-2">Nenhuma fatura encontrada</h3>
                        <p className="text-gray-500">
                            As faturas aparecerão aqui quando você tiver assinaturas ativas
                        </p>
                    </div>
                )}
            </div>

            {/* Quick Actions */}
            <div className="grid gap-4 md:grid-cols-3">
                <QuickAction
                    icon={<CreditCard className="w-6 h-6" />}
                    title="Gerar Cobrança"
                    description="Crie uma cobrança avulsa para um atleta"
                    onClick={() => setShowCreateModal(true)}
                />
                <QuickAction
                    icon={<RefreshCw className="w-6 h-6" />}
                    title="Reenviar Cobrança"
                    description="Reenvie uma cobrança pendente por email/WhatsApp"
                    onClick={() => alert('Em breve!')}
                />
                <QuickAction
                    icon={<Download className="w-6 h-6" />}
                    title="Exportar Relatório"
                    description="Baixe um relatório financeiro em Excel"
                    onClick={() => alert('Em breve!')}
                />
            </div>

            <CreateChargeModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
            />
        </div>
    );
}

// Stat Card Component
function StatCard({
    title,
    value,
    icon,
    trend,
    color,
}: {
    title: string;
    value: string;
    icon: React.ReactNode;
    trend?: number;
    color: 'primary' | 'green' | 'yellow' | 'purple';
}) {
    const colorClasses = {
        primary: 'bg-primary-100 text-primary-600',
        green: 'bg-green-100 text-green-600',
        yellow: 'bg-yellow-100 text-yellow-600',
        purple: 'bg-purple-100 text-purple-600',
    };

    return (
        <div className="card">
            <div className="flex items-start justify-between">
                <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
                    {icon}
                </div>
                {trend !== undefined && (
                    <div
                        className={`flex items-center gap-1 text-sm ${trend >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}
                    >
                        {trend >= 0 ? (
                            <ArrowUpRight className="w-4 h-4" />
                        ) : (
                            <ArrowDownRight className="w-4 h-4" />
                        )}
                        <span>{Math.abs(trend)}%</span>
                    </div>
                )}
            </div>
            <div className="mt-4">
                <p className="text-sm text-gray-500">{title}</p>
                <p className="text-2xl font-bold text-gray-900">{value}</p>
            </div>
        </div>
    );
}

// Quick Action Component
function QuickAction({
    icon,
    title,
    description,
    onClick,
}: {
    icon: React.ReactNode;
    title: string;
    description: string;
    onClick: () => void;
}) {
    return (
        <button
            onClick={onClick}
            className="card text-left hover:border-primary-300 hover:shadow-md transition-all group"
        >
            <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-primary-50 text-primary-600 group-hover:bg-primary-100 transition-colors">
                    {icon}
                </div>
                <div>
                    <h3 className="font-semibold text-gray-900">{title}</h3>
                    <p className="text-sm text-gray-500">{description}</p>
                </div>
            </div>
        </button>
    );
}
